import { useMemo } from 'react';
import { cn } from '@reflex/ui/lib/utils';
import { LogEmptyState } from './LogEmptyState';
import { LEVEL_STYLES, LEVEL_BG, parseStructuredLine } from './log-viewer-utils';

export function StructuredViewer({ lines, filter }: { lines: string[]; filter: string }) {
  const parsed = useMemo(() => lines.map(parseStructuredLine), [lines]);

  const filtered = useMemo(() => {
    if (!filter) return parsed;
    const lower = filter.toLowerCase();
    return parsed.filter((l) => l.raw.toLowerCase().includes(lower));
  }, [parsed, filter]);

  return (
    <div className="font-mono text-2xs leading-[1.6]">
      {filtered.map((line, i) => (
        <div
          key={`${i}:${line.timestamp ?? line.raw.slice(0, 40)}`}
          className={cn(
            'flex gap-0 border-l-2 px-2 py-px hover:bg-foreground/2 transition-colors',
            line.level ? (LEVEL_BG[line.level] ?? 'border-l-transparent') : 'border-l-transparent',
          )}
        >
          <span className="text-muted-foreground w-6 text-right shrink-0 select-none mr-3">
            {i + 1}
          </span>
          {line.timeShort ? (
            <>
              <span className="text-muted-foreground shrink-0 mr-2" title={line.timestamp}>
                {line.timeShort}
              </span>
              {line.level && (
                <span
                  className={cn(
                    'w-10 shrink-0 mr-1',
                    LEVEL_STYLES[line.level] ?? 'text-muted-foreground',
                  )}
                >
                  [{line.level}]
                </span>
              )}
              {line.context && line.context !== 'null' && (
                <span className="text-muted-foreground shrink-0 mr-2">[{line.context}]</span>
              )}
              <span className="text-foreground/80 break-all">{line.message}</span>
            </>
          ) : (
            <span className="text-foreground/70 break-all">{line.raw}</span>
          )}
        </div>
      ))}
      {filtered.length === 0 && <LogEmptyState>No matching lines</LogEmptyState>}
    </div>
  );
}
